import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import Badge from "../components/brand/Badge";
import KpiTile from "../components/brand/KpiTile";
import { useAuth } from "../context/AuthContext";
import {
  getFeedback,
  getFeedbackStats,
  createFeedback,
  updateFeedback,
  getFeedbackComments,
  createFeedbackComment,
  getFeedbackDocuments,
  uploadFeedbackDocument,
  updateFeedbackDocument,
  deleteFeedbackDocument,
} from "../api/feedbackApi";
import { getUsersForChangeRequests } from "../api/userApi";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { fonts } from "../theme/tokens";
import { formatDateUk } from "../utils/dateDisplay";

const FEEDBACK_TYPE_OPTIONS = ["Change Request", "Feedback"];
const STATUS_OPTIONS = ["New", "In Progress", "Blocked", "Done"];
const PAGE_SIZE = 10;
const DOCUMENT_PAGE_SIZE = 10;
const SUMMARY_FILTERS = {
  all: { label: "All" },
  open: { label: "Open", statuses: ["New", "In Progress"] },
  blocked: { label: "Blocked", statuses: ["Blocked"] },
  done: { label: "Done", statuses: ["Done"] },
  unassigned: { label: "Unassigned", unassigned: true },
};

const extractApiErrorMessage = (err, fallbackMessage) => {
  const message = err?.response?.data?.error || err?.response?.data?.detail;
  return typeof message === "string" && message.trim() ? message : fallbackMessage;
};

function FeedbackPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({
    total: 0,
    new: 0,
    in_progress: 0,
    blocked: 0,
    done: 0,
    unassigned: 0,
  });
  const [users, setUsers] = useState([]);
  const [hoverComments, setHoverComments] = useState({});
  const [hoverLoading, setHoverLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsError, setCommentsError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsTotalPages, setDocumentsTotalPages] = useState(1);
  const [queuedDocuments, setQueuedDocuments] = useState([]);
  const [documentDrafts, setDocumentDrafts] = useState({});
  const [savingDocumentIds, setSavingDocumentIds] = useState({});
  const [deletingDocumentIds, setDeletingDocumentIds] = useState({});
  const [, setDocumentsTotalCount] = useState(0);
  const [formError, setFormError] = useState("");
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sortField, setSortField] = useState("created_at_utc");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
  const [summaryFilter, setSummaryFilter] = useState("open");

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "",
    status: "New",
    assigned_to_id: "",
  });

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    type: "Change Request",
    status: "New",
    assigned_to_id: "",
  });
  const editFormInitialRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const queuedDocumentIdRef = useRef(0);

  const userLabel = useMemo(() => user?.email || user?.name || user?.username || "Unknown", [user]);

  const selectFieldSx = {
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      fontSize: 12.5,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiInputLabel-root": { fontSize: 12.5 },
  };

  const loadUsers = useCallback(async () => {
    try {
      const userData = await getUsersForChangeRequests();
      setUsers(userData || []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, []);

  const loadFeedbackStats = useCallback(async () => {
    try {
      const stats = await getFeedbackStats();
      setFeedbackStats({
        total: stats?.total ?? 0,
        new: stats?.new ?? 0,
        in_progress: stats?.in_progress ?? 0,
        blocked: stats?.blocked ?? 0,
        done: stats?.done ?? 0,
        unassigned: stats?.unassigned ?? 0,
      });
    } catch (err) {
      console.error("Failed to load feedback stats:", err);
    }
  }, []);

  const loadFeedback = useCallback(async (overrides = {}) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);
    setError("");
    try {
      const summaryConfig = summaryFilter ? SUMMARY_FILTERS[summaryFilter] : null;
      const params = {
        page: overrides.page ?? page,
        page_size: PAGE_SIZE,
        sort: overrides.sortField ?? sortField,
        dir: overrides.sortDir ?? sortDir,
      };

      // Match Sort / Direction: changing status must not fight summary KPIs.
      // When a specific status is chosen in the dropdown, it wins; otherwise summary / unassigned apply.
      if (selectedStatusFilter) {
        params.status = selectedStatusFilter;
      } else if (summaryConfig?.statuses?.length) {
        params.status = summaryConfig.statuses.join(",");
      } else if (summaryConfig?.unassigned) {
        params.unassigned = true;
      }

      const response = await getFeedback(params);
      if (loadRequestIdRef.current === requestId) {
        setFeedbackItems(response?.results || []);
        setTotalPages(Number(response?.total_pages || 1));
      }
    } catch (err) {
      console.error("Failed to load feedback:", err);
      if (loadRequestIdRef.current === requestId) {
        setError("Unable to load feedback. Please try again later.");
      }
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [page, selectedStatusFilter, sortDir, sortField, summaryFilter]);

  const loadComments = async (feedbackId) => {
    setComments([]);
    setCommentsError("");
    try {
      const commentRes = await getFeedbackComments(feedbackId);
      setComments(commentRes || []);
    } catch (err) {
      console.error("Failed to load comments:", err);
      setCommentsError("Unable to load comments. Please try again later.");
    }
  };

  const loadDocuments = useCallback(async (feedbackId, pageNumber = 1) => {
    setDocumentsLoading(true);
    setDocumentsError("");
    try {
      const response = await getFeedbackDocuments(feedbackId, {
        page: pageNumber,
        page_size: DOCUMENT_PAGE_SIZE,
        sort: "updated_at_utc",
        dir: "desc",
      });
      const nextDocuments = response?.results || [];
      setDocuments(nextDocuments);
      setDocumentDrafts((prev) => {
        const allowedIds = new Set(nextDocuments.map((doc) => doc.id));
        return Object.fromEntries(Object.entries(prev).filter(([id]) => allowedIds.has(Number(id))));
      });
      setDocumentsTotalCount(Number(response?.total || 0));
      setDocumentsTotalPages(Number(response?.total_pages || 1));
      setDocumentsPage(Number(response?.page || pageNumber));
    } catch (err) {
      console.error("Failed to load documents:", err);
      setDocumentsError("Unable to load documents. Please try again later.");
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadFeedbackStats();
  }, [loadUsers, loadFeedbackStats]);

  useEffect(() => {
    loadFeedback({ page });
  }, [loadFeedback, page]);

  const handleOpenCreate = () => {
    setFormError("");
    setForm({
      title: "",
      description: "",
      type: "",
      status: "New",
      assigned_to_id: "",
    });
    setDialogOpen(true);
  };

  const applySummaryFilter = (nextFilter) => {
    setSummaryFilter((current) => (current === nextFilter ? null : nextFilter));
    setSelectedStatusFilter("");
    setPage(1);
  };

  const applyStatusFilter = (status) => {
    setSelectedStatusFilter((current) => (current === status ? "" : status));
    setPage(1);
  };

  const handleCreate = async () => {
    setFormError("");
    setSubmitting(true);
    try {
      await createFeedback({
        title: form.title,
        description: form.description,
        type: form.type,
        status: form.status,
        assigned_to_id: form.assigned_to_id || null,
      });
      setDialogOpen(false);
      setPage(1);
      await loadFeedbackStats();
      await loadFeedback({ page: 1 });
    } catch (err) {
      console.error("Failed to create feedback:", err);
      setFormError(extractApiErrorMessage(err, "Unable to create feedback. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDetail = async (item) => {
    const nextEditForm = {
      title: item.title || "",
      description: item.description || "",
      type: item.type || "Change Request",
      status: item.status || "New",
      assigned_to_id: item.assigned_to_id || "",
    };
    setSelectedFeedback(item);
    setEditForm(nextEditForm);
    editFormInitialRef.current = nextEditForm;
    setCommentText("");
    setDocuments([]);
    setQueuedDocuments([]);
    setDocumentDrafts({});
    setDocumentsPage(1);
    setDocumentsTotalPages(1);
    setDetailOpen(true);
    await Promise.all([loadComments(item.id), loadDocuments(item.id, 1)]);
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years === 1 ? "" : "s"} ago`;
  };

  const ensureHoverComments = async (feedbackId) => {
    if (hoverComments[feedbackId] || hoverLoading[feedbackId]) {
      return;
    }
    setHoverLoading((prev) => ({ ...prev, [feedbackId]: true }));
    try {
      const data = await getFeedbackComments(feedbackId);
      const ordered = (data || []).slice().sort((a, b) => {
        return new Date(b.created_at_utc) - new Date(a.created_at_utc);
      });
      setHoverComments((prev) => ({ ...prev, [feedbackId]: ordered }));
    } catch (err) {
      console.error("Failed to load feedback comments:", err);
    } finally {
      setHoverLoading((prev) => ({ ...prev, [feedbackId]: false }));
    }
  };

  const normalizeEditForm = (formState) => ({
    title: formState.title.trim(),
    description: formState.description.trim(),
    type: formState.type || "Change Request",
    status: formState.status || "New",
    assigned_to_id: formState.assigned_to_id ? Number(formState.assigned_to_id) : null,
  });

  const isEditDirty = useMemo(() => {
    if (!editFormInitialRef.current) return false;
    const initial = normalizeEditForm(editFormInitialRef.current);
    const current = normalizeEditForm(editForm);
    return JSON.stringify(initial) !== JSON.stringify(current);
  }, [editForm]);

  const handleSave = async () => {
    if (!selectedFeedback) {
      return;
    }
    setSubmitting(true);
    try {
      await updateFeedback(selectedFeedback.id, {
        title: editForm.title,
        description: editForm.description,
        type: editForm.type,
        status: editForm.status,
        assigned_to_id: editForm.assigned_to_id || null,
      });
      await loadFeedbackStats();
      await loadFeedback({ page });
      setDetailOpen(false);
    } catch (err) {
      console.error("Failed to update feedback:", err);
      setError("Unable to update feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedFeedback || !commentText.trim()) {
      return;
    }
    setSubmitting(true);
    try {
      await createFeedbackComment(selectedFeedback.id, { comment_text: commentText.trim() });
      setCommentText("");
      await loadComments(selectedFeedback.id);
      await loadFeedback({ page });
    } catch (err) {
      console.error("Failed to create comment:", err);
      setCommentsError("Unable to add comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const queueSelectedDocuments = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    if (!nextFiles.length) {
      return;
    }
    setDocumentsError("");
    setQueuedDocuments((prev) => [
      ...nextFiles.map((file) => {
        queuedDocumentIdRef.current += 1;
        return {
          id: `queued-${queuedDocumentIdRef.current}`,
          file,
          file_name: file.name,
          comment_text: "",
          updated_at_utc: null,
          updated_by_email: user?.email || "",
          updated_by_name: user?.name || user?.username || "",
          status: "pending",
          error: "",
        };
      }),
      ...prev,
    ]);
  };

  const updateQueuedDocument = (queuedId, updates) => {
    setQueuedDocuments((prev) =>
      prev.map((doc) => (doc.id === queuedId ? { ...doc, ...updates } : doc))
    );
  };

  const uploadQueuedDocument = async (queuedId, commentOverride) => {
    if (!selectedFeedback) {
      return;
    }

    const queuedDocument = queuedDocuments.find((doc) => doc.id === queuedId);
    if (!queuedDocument || queuedDocument.status === "uploading") {
      return;
    }

    const commentTextValue = (commentOverride ?? queuedDocument.comment_text).trim();
    if (!commentTextValue) {
      updateQueuedDocument(queuedId, { error: "Comment is required." });
      return;
    }

    updateQueuedDocument(queuedId, { status: "uploading", error: "" });
    setDocumentsError("");

    try {
      const response = await uploadFeedbackDocument(selectedFeedback.id, queuedDocument.file, commentTextValue);
      const uploadedDocument = response?.document;
      setQueuedDocuments((prev) => prev.filter((doc) => doc.id !== queuedId));
      if (uploadedDocument) {
        setDocuments((prev) => {
          if (documentsPage !== 1) {
            return prev;
          }
          return [uploadedDocument, ...prev].slice(0, DOCUMENT_PAGE_SIZE);
        });
        setDocumentsTotalCount((prev) => {
          const nextTotal = prev + 1;
          setDocumentsTotalPages(Math.max(1, Math.ceil(nextTotal / DOCUMENT_PAGE_SIZE)));
          return nextTotal;
        });
      }
    } catch (err) {
      console.error("Failed to upload document:", err);
      updateQueuedDocument(queuedId, {
        status: "error",
        error: extractApiErrorMessage(err, "Unable to upload document. Please try again."),
      });
    }
  };

  const updateExistingDocumentComment = async (documentId, commentOverride) => {
    if (!selectedFeedback) {
      return;
    }

    const document = documents.find((doc) => doc.id === documentId);
    const draftValue = commentOverride ?? documentDrafts[documentId];
    if (!document || draftValue === undefined) {
      return;
    }

    const commentTextValue = draftValue.trim();
    if (!commentTextValue) {
      return;
    }
    if (commentTextValue === (document.comment_text || "").trim()) {
      return;
    }

    setSavingDocumentIds((prev) => ({ ...prev, [documentId]: true }));
    setDocumentsError("");

    try {
      const response = await updateFeedbackDocument(selectedFeedback.id, documentId, {
        comment_text: commentTextValue,
      });
      const updatedDocument = response?.document;

      setDocuments((prev) =>
        prev.map((doc) => (doc.id === documentId ? { ...doc, ...updatedDocument } : doc))
      );
      setDocumentDrafts((prev) => {
        const nextDrafts = { ...prev };
        delete nextDrafts[documentId];
        return nextDrafts;
      });
    } catch (err) {
      console.error("Failed to update document:", err);
      setDocumentsError(extractApiErrorMessage(err, "Unable to update document. Please try again."));
    } finally {
      setSavingDocumentIds((prev) => {
        const nextSaving = { ...prev };
        delete nextSaving[documentId];
        return nextSaving;
      });
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!selectedFeedback || !doc) {
      return;
    }

    if (doc.file) {
      setQueuedDocuments((prev) => prev.filter((queuedDoc) => queuedDoc.id !== doc.id));
      return;
    }

    setDeletingDocumentIds((prev) => ({ ...prev, [doc.id]: true }));
    setDocumentsError("");

    try {
      await deleteFeedbackDocument(selectedFeedback.id, doc.id);
      const remainingDocuments = documents.filter((existingDoc) => existingDoc.id !== doc.id);
      setDocuments(remainingDocuments);
      setDocumentDrafts((prev) => {
        const nextDrafts = { ...prev };
        delete nextDrafts[doc.id];
        return nextDrafts;
      });
      setDocumentsTotalCount((prev) => {
        const nextTotal = Math.max(0, prev - 1);
        setDocumentsTotalPages(Math.max(1, Math.ceil(nextTotal / DOCUMENT_PAGE_SIZE)));
        return nextTotal;
      });

      if (remainingDocuments.length === 0 && documentsPage > 1) {
        await loadDocuments(selectedFeedback.id, documentsPage - 1);
      }
    } catch (err) {
      console.error("Failed to delete document:", err);
      setDocumentsError(extractApiErrorMessage(err, "Unable to delete document. Please try again."));
    } finally {
      setDeletingDocumentIds((prev) => {
        const nextDeleting = { ...prev };
        delete nextDeleting[doc.id];
        return nextDeleting;
      });
    }
  };

  const documentRows = useMemo(() => [...queuedDocuments, ...documents], [documents, queuedDocuments]);

  const hasDocumentDraftChanges = (doc) => {
    if (doc.file) {
      return Boolean(getDocumentCommentValue(doc).trim()) && doc.status !== "uploading";
    }
    const draftValue = documentDrafts[doc.id];
    if (draftValue === undefined) {
      return false;
    }
    return draftValue.trim() !== (doc.comment_text || "").trim();
  };

  const crRowGrid = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "minmax(0, 1.2fr) 132px 112px minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 1fr)",
    },
    gap: { xs: 0.5, md: 1.75 },
    alignItems: "center",
  };

  const statusBadgeTone = (status) => {
    if (status === "Blocked") return "High";
    if (status === "Done") return "Closed";
    if (status === "New") return "Open";
    if (status === "In Progress") return "In Progress";
    return "neutral";
  };

  const getDocumentCommentValue = (doc) =>
    doc.file ? doc.comment_text : documentDrafts[doc.id] ?? doc.comment_text ?? "";

  const isDocumentCommentMissing = (doc) => !getDocumentCommentValue(doc).trim();

  const getDocumentHelperText = (doc) => {
    if (doc.error) return doc.error;
    return " ";
  };

  return (
    <Layout
      eyebrow="Product · Support"
      title="Feedback"
      rightSlot={
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={handleOpenCreate}
          sx={{ px: 1.75, py: "8px", fontSize: 12.5 }}
        >
          New feedback
        </Button>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <KpiTile
          label="Total"
          value={feedbackStats.total}
          sub="All feedback"
          onClick={() => applySummaryFilter("all")}
          active={summaryFilter === "all" && !selectedStatusFilter}
        />
        <KpiTile
          label="Open"
          value={feedbackStats.new + feedbackStats.in_progress}
          sub="New + in progress"
          tone="warning"
          onClick={() => applySummaryFilter("open")}
          active={summaryFilter === "open" && !selectedStatusFilter}
        />
        <KpiTile
          label="In progress"
          value={feedbackStats.in_progress}
          sub="Actively being worked"
          onClick={() => applyStatusFilter("In Progress")}
          active={selectedStatusFilter === "In Progress"}
        />
        <KpiTile
          label="Blocked"
          value={feedbackStats.blocked}
          sub="External dependency"
          tone="danger"
          onClick={() => applySummaryFilter("blocked")}
          active={summaryFilter === "blocked" && !selectedStatusFilter}
        />
        <KpiTile
          label="Done"
          value={feedbackStats.done}
          sub="Completed"
          tone="success"
          onClick={() => applySummaryFilter("done")}
          active={summaryFilter === "done" && !selectedStatusFilter}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          mb: 2,
        }}
      >
        <FormControl size="small" sx={selectFieldSx}>
          <InputLabel>Status</InputLabel>
          <Select
            value={selectedStatusFilter}
            label="Status"
            onChange={(event) => {
              setSelectedStatusFilter(event.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={selectFieldSx}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortField}
            label="Sort by"
            onChange={(event) => {
              setSortField(event.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="created_at_utc">Created date</MenuItem>
            <MenuItem value="updated_at_utc">Updated date</MenuItem>
            <MenuItem value="title">Title</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="assigned_to_name">Assigned to</MenuItem>
            <MenuItem value="latest_comment_at">Latest comment</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...selectFieldSx, minWidth: 140 }}>
          <InputLabel>Direction</InputLabel>
          <Select
            value={sortDir}
            label="Direction"
            onChange={(event) => {
              setSortDir(event.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="desc">Descending</MenuItem>
            <MenuItem value="asc">Ascending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Panel
        eyebrow="Queue"
        title={
          loading ? "Loading…" : error ? "Feedback" : `${feedbackItems.length} feedback items`
        }
      >
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center", color: theme.palette.brand.inkSoft, fontSize: 13 }}>
            Loading feedback…
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center", color: theme.palette.error.main, fontSize: 13 }}>
            {error}
          </Box>
        ) : feedbackItems.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: theme.palette.brand.inkSoft, fontSize: 13 }}>
            No feedback items match this view.
          </Box>
        ) : (
          <>
            <Box
              sx={{
                ...crRowGrid,
                display: { xs: "none", md: "grid" },
                px: 2.75,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.brand.border}`,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: theme.palette.brand.inkSoft,
                fontFamily: fonts.mono,
              }}
            >
              <Box>Title</Box>
              <Box>Type</Box>
              <Box>Status</Box>
              <Box>Assigned</Box>
              <Box>Created by</Box>
              <Box>Latest comment</Box>
            </Box>

            {feedbackItems.map((item, i) => (
              <Box
                key={item.id}
                onClick={() => handleOpenDetail(item)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleOpenDetail(item);
                  }
                }}
                role="button"
                tabIndex={0}
                sx={{
                  ...crRowGrid,
                  px: { xs: 2, md: 2.75 },
                  py: { xs: 1.5, md: 1.75 },
                  borderBottom:
                    i < feedbackItems.length - 1 ? `1px solid ${theme.palette.brand.border}` : "none",
                  cursor: "pointer",
                  transition: "background .12s",
                  "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
                }}
              >
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: theme.palette.brand.ink,
                    lineHeight: 1.3,
                    minWidth: 0,
                    gridColumn: { xs: "1 / -1", md: "auto" },
                  }}
                >
                  {item.title?.length > 48 ? `${item.title.slice(0, 48)}…` : item.title}
                </Box>
                <Box sx={{ fontSize: 12.5, color: theme.palette.brand.inkMuted, minWidth: 0 }}>
                  {item.type || "—"}
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                  {item.status ? <Badge tone={statusBadgeTone(item.status)}>{item.status}</Badge> : "—"}
                </Box>
                <Box sx={{ fontSize: 12.5, color: theme.palette.brand.inkMuted, minWidth: 0 }}>
                  {item.assigned_to_name || "Unassigned"}
                </Box>
                <Box
                  sx={{
                    fontSize: 12.5,
                    color: theme.palette.brand.inkMuted,
                    minWidth: 0,
                    wordBreak: "break-word",
                  }}
                >
                  {item.created_by_email || item.created_by_name || "Unknown"}
                </Box>
                <Box sx={{ fontSize: 12, color: theme.palette.brand.inkSoft, minWidth: 0 }}>
                  {item.latest_comment_at ? (
                    <Tooltip
                      title={
                        hoverComments[item.id]?.length ? (
                          <Box sx={{ maxWidth: 320 }}>
                            {hoverComments[item.id].map((comment) => (
                              <Box key={comment.id} sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {comment.created_by_email ||
                                    comment.created_by_name ||
                                    "Unknown"}{" "}
                                  · {formatRelativeTime(comment.created_at_utc)}
                                </Typography>
                                <Typography variant="body2">{comment.comment_text}</Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : hoverLoading[item.id] ? (
                          "Loading comments…"
                        ) : (
                          "No comments"
                        )
                      }
                      placement="top-start"
                      onOpen={() => ensureHoverComments(item.id)}
                    >
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                        {formatRelativeTime(item.latest_comment_at)}
                      </Box>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </Box>
              </Box>
            ))}
          </>
        )}
      </Panel>

      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} size="small" />
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: fonts.serif, fontWeight: 600, fontSize: 22 }}>
          New feedback
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {formError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="" disabled>
                  Select type
                </MenuItem>
                {FEEDBACK_TYPE_OPTIONS.map((feedbackType) => (
                  <MenuItem key={feedbackType} value={feedbackType}>
                    {feedbackType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                label="Assign To"
                value={form.assigned_to_id}
                onChange={(e) => setForm((prev) => ({ ...prev, assigned_to_id: e.target.value }))}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email || u.username || `User ${u.user_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            Submitting as {userLabel}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting || !form.title.trim() || !form.description.trim() || !form.type}
          >
            {submitting ? "Saving..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: "1150px" } }}
      >
        <DialogTitle sx={{ fontFamily: fonts.serif, fontWeight: 600, fontSize: 22 }}>
          Feedback details
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={editForm.title}
            onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
            fullWidth
            sx={{ mt: 2 }}
          />
          <TextField
            label="Description"
            value={editForm.description}
            onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={editForm.type}
                onChange={(e) => setEditForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="">Not set</MenuItem>
                {FEEDBACK_TYPE_OPTIONS.map((feedbackType) => (
                  <MenuItem key={feedbackType} value={feedbackType}>
                    {feedbackType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editForm.status}
                onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                label="Assign To"
                value={editForm.assigned_to_id}
                onChange={(e) => setEditForm((prev) => ({ ...prev, assigned_to_id: e.target.value }))}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.user_id} value={u.user_id}>
                    {u.name || u.email || u.username || `User ${u.user_id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3, borderColor: theme.palette.brand.border }} />

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontFamily: fonts.serif, fontSize: 16, fontWeight: 600 }}>
              Documents
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => document.getElementById("feedback-document-upload")?.click()}
            >
              Add files
            </Button>
          </Box>

          {documentsError ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {documentsError}
            </Alert>
          ) : null}
          <input
            id="feedback-document-upload"
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(event) => {
              queueSelectedDocuments(event.target.files);
              event.target.value = "";
            }}
          />

          {documentsLoading ? (
            <Box sx={{ mb: 2, py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : documentRows.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No documents yet.
            </Typography>
          ) : (
            <>
              <TableContainer sx={{ mb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell>Date updated</TableCell>
                      <TableCell>Who by</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentRows.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {doc.download_url ? (
                            <Box
                              component="a"
                              href={doc.download_url}
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                color: theme.palette.primary.main,
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {doc.file_name || `Document ${doc.id}`}
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">
                                {doc.file_name || `Document ${doc.id}`}
                              </Typography>
                              {doc.file ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: "999px",
                                    backgroundColor:
                                      doc.status === "uploading"
                                        ? theme.palette.info.light
                                        : theme.palette.warning.light,
                                    color:
                                      doc.status === "uploading"
                                        ? theme.palette.info.contrastText
                                        : theme.palette.warning.contrastText,
                                  }}
                                >
                                  {doc.status === "uploading" ? "Uploading" : "Pending"}
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 280, verticalAlign: "top" }}>
                          <TextField
                            value={getDocumentCommentValue(doc)}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              if (doc.file) {
                                updateQueuedDocument(doc.id, {
                                  comment_text: nextValue,
                                  error: "",
                                  status: doc.status === "error" ? "pending" : doc.status,
                                });
                                return;
                              }
                              setDocumentDrafts((prev) => ({ ...prev, [doc.id]: nextValue }));
                            }}
                            onBlur={() => {
                              const currentValue = getDocumentCommentValue(doc);
                              if (doc.file) {
                                uploadQueuedDocument(doc.id, currentValue);
                                return;
                              }
                              updateExistingDocumentComment(doc.id, currentValue);
                            }}
                            placeholder="Add a document comment"
                            fullWidth
                            error={isDocumentCommentMissing(doc) || Boolean(doc.error)}
                            helperText={getDocumentHelperText(doc)}
                            disabled={doc.status === "uploading" || Boolean(savingDocumentIds[doc.id])}
                            InputProps={{
                              endAdornment: isDocumentCommentMissing(doc) ? (
                                <InputAdornment position="end">
                                  <Tooltip title="Comment required">
                                    <WarningAmberRoundedIcon
                                      sx={{ color: theme.palette.warning.main, fontSize: 18 }}
                                    />
                                  </Tooltip>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {doc.updated_at_utc ? formatDateUk(doc.updated_at_utc) : "Waiting for upload"}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {doc.updated_by_email || doc.updated_by_name || (doc.file ? userLabel : "Unknown")}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                          {doc.status === "uploading" ||
                          savingDocumentIds[doc.id] ||
                          deletingDocumentIds[doc.id] ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  const currentValue = getDocumentCommentValue(doc);
                                  if (doc.file) {
                                    uploadQueuedDocument(doc.id, currentValue);
                                    return;
                                  }
                                  updateExistingDocumentComment(doc.id, currentValue);
                                }}
                                disabled={!hasDocumentDraftChanges(doc) || isDocumentCommentMissing(doc)}
                              >
                                Save
                              </Button>
                              <Tooltip title="Delete document">
                                <span>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => handleDeleteDocument(doc)}
                                    disabled={doc.status === "uploading"}
                                    sx={{ minWidth: 36, px: 0.5 }}
                                  >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                  </Button>
                                </span>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {documentsTotalPages > 1 ? (
                <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                  <Pagination
                    count={documentsTotalPages}
                    page={documentsPage}
                    onChange={(_, value) => {
                      if (selectedFeedback) {
                        loadDocuments(selectedFeedback.id, value);
                      }
                    }}
                    size="small"
                  />
                </Box>
              ) : null}
            </>
          )}

          <Divider sx={{ my: 3, borderColor: theme.palette.brand.border }} />

          <Typography sx={{ fontFamily: fonts.serif, fontSize: 16, fontWeight: 600, mb: 1 }}>
            Comments
          </Typography>
          {commentsError && (
            <Typography color="error" sx={{ mb: 1 }}>
              {commentsError}
            </Typography>
          )}
          {comments.length === 0 ? (
            <Typography color="text.secondary">No comments yet.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {comments.map((comment) => (
                <Box
                  key={comment.id}
                  sx={{
                    p: 1.5,
                    borderRadius: "6px",
                    backgroundColor: theme.palette.brand.surfaceMuted,
                    border: `1px solid ${theme.palette.brand.border}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {comment.created_by_email || comment.created_by_name || "Unknown"} •{" "}
                    {comment.created_at_utc
                      ? new Date(comment.created_at_utc).toLocaleString()
                      : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {comment.comment_text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <TextField
            label={`Add comment as ${userLabel}`}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailOpen(false)} disabled={submitting}>
            Close
          </Button>
          <Button
            onClick={handleAddComment}
            disabled={submitting || !commentText.trim()}
          >
            {submitting ? "Saving..." : "Add Comment"}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              submitting ||
              !editForm.title.trim() ||
              !editForm.description.trim() ||
              !isEditDirty
            }
          >
            {submitting ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default FeedbackPage;
